import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';

type SubmitStatus = 'idle' | 'success' | 'error';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  automation_challenge: string;
  budget: string;
  timeline: string;
  role: string;
  company_size: string;
  preferred_time: string;
}

const QualificationForm: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    automation_challenge: '',
    budget: '',
    timeline: '',
    role: '',
    company_size: '',
    preferred_time: '',
  });

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle');

  // Pre-fill form from URL parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.get('source') === 'metaknow-funnel') {
      const name = urlParams.get('name');
      const email = urlParams.get('email');
      const challenge = urlParams.get('automation_challenge');
      
      setFormData((prev) => ({
        ...prev,
        ...(name && { name }),
        ...(email && { email }),
        ...(challenge && { automation_challenge: challenge }),
      }));
      
      setTimeout(() => {
        const form = document.getElementById('consultation-form');
        if (form) {
          form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 500);
    }
  }, []);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://metaknowlabs.app.n8n.cloud/webhook-test/7b63a577-ffba-4f5b-8f95-3cc6c58a0b01', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          automation_challenge: '',
          budget: '',
          timeline: '',
          role: '',
          company_size: '',
          preferred_time: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
    padding: '1.5rem',
    backgroundColor: '#0a0a0a',
    border: '1px solid #33ff33',
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column'
  };

  const labelStyle: React.CSSProperties = {
    color: '#ccc',
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem'
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '4px',
    color: '#fff',
    fontSize: '1rem',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s'
  };

  const fieldContainerStyle: React.CSSProperties = {
    marginBottom: '1.5rem',
    textAlign: 'left'
  };

  const radioLabelStyle: React.CSSProperties = {
    cursor: 'pointer',
    display: 'block',
    marginBottom: '0.5rem',
    color: '#ccc',
    fontSize: '0.875rem'
  };

  return (
    <div id="consultation-form" style={containerStyle} className="qualification-form-container">
      <h2 style={{ color: '#33ff33', textAlign: 'center', marginBottom: '1rem', fontSize: 'clamp(1.25rem, 5vw, 1.875rem)', fontWeight: 800 }}>
        Schedule an Architecture Consultation
      </h2>
      <p style={{ color: '#ccc', textAlign: 'center', marginBottom: '1.5rem', fontSize: '0.875rem', lineHeight: '1.5' }}>
        Tell us about your automation needs and we'll reach out to qualified prospects within 24 hours.
      </p>

      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Full Name <span style={{ color: '#33ff33' }}>*</span></label>
          <input
            type="text"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => e.currentTarget.style.borderColor = '#33ff33'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
          />
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Email Address <span style={{ color: '#33ff33' }}>*</span></label>
          <input
            type="email"
            name="email"
            required
            value={formData.email}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => e.currentTarget.style.borderColor = '#33ff33'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
          />
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Phone Number <span style={{ color: '#33ff33' }}>*</span></label>
          <input
            type="tel"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => e.currentTarget.style.borderColor = '#33ff33'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
          />
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Company Name</label>
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => e.currentTarget.style.borderColor = '#33ff33'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
          />
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>What automation challenge are you solving? <span style={{ color: '#33ff33' }}>*</span></label>
          <textarea
            name="automation_challenge"
            required
            minLength={50}
            rows={4}
            value={formData.automation_challenge}
            onChange={handleChange}
            placeholder="Example: Our sales team wastes 10 hours/week on manual data entry..."
            style={{ ...inputStyle, resize: 'none' }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#33ff33'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
          />
          <p style={{ color: '#666', fontSize: '0.75rem', marginTop: '0.25rem' }}>Minimum 50 characters</p>
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Have you allocated budget? <span style={{ color: '#33ff33' }}>*</span></label>
          <div style={{ paddingLeft: '0.25rem' }}>
            {[
              { value: 'approved', label: 'Yes, we have budget approved' },
              { value: 'planning', label: "We're in the budget planning phase" },
              { value: 'exploring', label: 'Still exploring options' },
              { value: 'no_budget', label: 'No budget at this time' }
            ].map((opt) => (
              <label key={opt.value} style={radioLabelStyle}>
                <input
                  type="radio"
                  name="budget"
                  required
                  value={opt.value}
                  checked={formData.budget === opt.value}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem', accentColor: '#33ff33' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>What's your timeline? <span style={{ color: '#33ff33' }}>*</span></label>
          <select
            name="timeline"
            required
            value={formData.timeline}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => e.currentTarget.style.borderColor = '#33ff33'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
          >
            <option value="">Select timeline...</option>
            <option value="30_days">Ready to start within 30 days</option>
            <option value="30_60_days">30-60 days</option>
            <option value="60_90_days">60-90 days</option>
            <option value="3_6_months">3-6 months</option>
            <option value="researching">Just researching for now</option>
          </select>
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>What's your role in this decision? <span style={{ color: '#33ff33' }}>*</span></label>
          <div style={{ paddingLeft: '0.25rem' }}>
            {[
              { value: 'decision_maker', label: "I'm the decision maker" },
              { value: 'team_member', label: "I'm part of the decision-making team" },
              { value: 'recommender', label: 'I can recommend to the decision maker' },
              { value: 'gathering_info', label: 'Just gathering information' }
            ].map((opt) => (
              <label key={opt.value} style={radioLabelStyle}>
                <input
                  type="radio"
                  name="role"
                  required
                  value={opt.value}
                  checked={formData.role === opt.value}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem', accentColor: '#33ff33' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Company size? <span style={{ color: '#33ff33' }}>*</span></label>
          <select
            name="company_size"
            required
            value={formData.company_size}
            onChange={handleChange}
            style={inputStyle}
            onFocus={(e) => e.currentTarget.style.borderColor = '#33ff33'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#333'}
          >
            <option value="">Select size...</option>
            <option value="solo">Solo/Freelancer (1 person)</option>
            <option value="small_team">Small team (2-10 people)</option>
            <option value="small_business">Small business (11-50 people)</option>
            <option value="mid_size">Mid-size (51-200 people)</option>
            <option value="enterprise">Enterprise (200+ people)</option>
          </select>
        </div>

        <div style={fieldContainerStyle}>
          <label style={labelStyle}>Preferred call time (optional)</label>
          <div style={{ paddingLeft: '0.25rem' }}>
            {[
              { value: 'morning', label: 'Morning (9am-12pm)' },
              { value: 'afternoon', label: 'Afternoon (12pm-5pm)' },
              { value: 'evening', label: 'Evening (5pm-8pm)' },
              { value: 'any', label: 'Any available slot' }
            ].map((opt) => (
              <label key={opt.value} style={radioLabelStyle}>
                <input
                  type="radio"
                  name="preferred_time"
                  value={opt.value}
                  checked={formData.preferred_time === opt.value}
                  onChange={handleChange}
                  style={{ marginRight: '0.5rem', accentColor: '#33ff33' }}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            width: '100%',
            backgroundColor: '#33ff33',
            color: '#000',
            padding: '1rem',
            border: 'none',
            borderRadius: '4px',
            fontSize: '1rem',
            fontWeight: 800,
            cursor: isSubmitting ? 'not-allowed' : 'pointer',
            opacity: isSubmitting ? 0.6 : 1,
            transition: 'background-color 0.2s',
          }}
        >
          {isSubmitting ? 'SUBMITTING...' : 'SUBMIT REQUEST'}
        </button>

        {submitStatus === 'success' && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#1a3a1a', border: '1px solid #33ff33', color: '#33ff33', borderRadius: '4px', textAlign: 'center' }}>
            ✓ Thank you! We'll contact qualified prospects within 24 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#3a1a1a', border: '1px solid #ff3333', color: '#ff3333', borderRadius: '4px', textAlign: 'center' }}>
            ✗ Something went wrong. Please try again or email us at devon@metaknow.org
          </div>
        )}
      </form>
    </div>
  );
};

export default QualificationForm;